/**
 * Path simplification for strokes that are not being drawn right now
 * (docs/03-ARCHITECTURE.md §7.4).
 *
 * A stroke recorded at a tablet's full sample rate can hold thousands of points. At
 * 50% zoom most of those land on the same screen pixel, so drawing them all costs
 * time and shows nothing. Douglas–Peucker keeps the points that carry the shape and
 * drops the ones that do not; the stroke itself is never altered, only what is drawn.
 */

import type { InkPoint } from './ink.js';

/** Perpendicular distance from a point to the line through `start` and `end`. */
function distanceToLine(point: InkPoint, start: InkPoint, end: InkPoint): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return Math.hypot(point.x - start.x, point.y - start.y);
  const cross = Math.abs(dx * (start.y - point.y) - dy * (start.x - point.x));
  return cross / length;
}

function keepBetween(
  points: readonly InkPoint[],
  first: number,
  last: number,
  tolerance: number,
  keep: boolean[],
): void {
  let worst = 0;
  let at = first;
  const start = points[first];
  const end = points[last];
  if (!start || !end) return;

  for (let index = first + 1; index < last; index += 1) {
    const point = points[index];
    if (!point) continue;
    const distance = distanceToLine(point, start, end);
    if (distance > worst) {
      worst = distance;
      at = index;
    }
  }

  if (worst <= tolerance) return;
  keep[at] = true;
  keepBetween(points, first, at, tolerance, keep);
  keepBetween(points, at, last, tolerance, keep);
}

/**
 * The same line drawn with fewer points. Ends are always kept, so a stroke never
 * shrinks or shifts.
 */
export function simplifyPoints(points: readonly InkPoint[], tolerance: number): InkPoint[] {
  if (points.length <= 2 || tolerance <= 0) return [...points];

  const keep = points.map((_, index) => index === 0 || index === points.length - 1);
  keepBetween(points, 0, points.length - 1, tolerance, keep);
  return points.filter((_, index) => keep[index] === true);
}

/**
 * How far a point may stray before it matters, in canvas units.
 *
 * Half a screen pixel: below that the difference cannot be seen, whatever the zoom.
 */
export function toleranceFor(zoom: number): number {
  return 0.5 / Math.max(zoom, 0.01);
}
